<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg"></div>
      <div class="container hero-inner">
        <div class="hero-content">
          <div class="hero-badge animate-on-scroll">
            <span class="badge-dot"></span>
            观影 · 阅读 · 游戏 · 笔记
          </div>
          <h1 class="hero-title animate-on-scroll aos-delay-1">每一种热爱，<br>都值得被认真记录</h1>
          <p class="hero-desc animate-on-scroll aos-delay-2">
            MookNote 是一款极简风格的媒体记录应用，涵盖影视、阅读、游戏与笔记。
            数据本地存储，支持 WebDAV 云同步，让回忆有迹可循。
          </p>
          <div class="hero-actions animate-on-scroll aos-delay-3">
            <a href="#download" class="btn btn-primary" @click.prevent="scrollToDownload">
              立即下载
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                <line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/>
              </svg>
            </a>
            <router-link to="/features" class="btn btn-secondary">了解功能</router-link>
          </div>
          <div class="hero-meta animate-on-scroll aos-delay-4">
            <span class="meta-pill">{{ latestVersion }}</span>
            <span class="meta-item">Android · Windows</span>
            <span class="meta-item">开源免费</span>
            <span class="meta-item">无需注册</span>
          </div>
        </div>
        <div class="hero-visual animate-on-scroll aos-delay-2">
          <div class="phone-mockup">
            <div class="phone-frame">
              <img src="~@/assets/img/screen_home.jpg" alt="MookNote 主界面" class="phone-screen" />
            </div>
          </div>
          <div class="float-card float-card-1">
            <div class="fc-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
              </svg>
            </div>
            <div class="fc-text">
              <span class="fc-value">128</span>
              <span class="fc-label">累计记录</span>
            </div>
          </div>
          <div class="float-card float-card-2">
            <div class="fc-icon accent">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div class="fc-text">
              <span class="fc-value">WebDAV</span>
              <span class="fc-label">云端已同步</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Modules -->
    <section class="modules">
      <div class="container">
        <span class="section-eyebrow animate-on-scroll" style="text-align:center">CORE MODULES</span>
        <h2 class="section-title animate-on-scroll">四大模块，一体收藏</h2>
        <p class="section-subtitle animate-on-scroll">光影、书页、游戏与灵感，各得其所</p>
        <div class="modules-grid">
          <div class="module-card animate-on-scroll">
            <div class="module-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="2" y="2" width="20" height="20" rx="2.18"/>
                <line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/>
                <line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/>
                <line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/>
              </svg>
            </div>
            <h3>影视记录</h3>
            <p>电影、剧集、动漫、纪录片，评分影评与海报墙，建立专属观影档案。</p>
            <div class="module-tags">
              <span>想看</span><span>在看</span><span>已看</span>
            </div>
          </div>
          <div class="module-card animate-on-scroll aos-delay-1">
            <div class="module-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            <h3>阅读管理</h3>
            <p>追踪阅读进度，书摘按章节归档，书评短评长评皆宜，见证每段阅读旅程。</p>
            <div class="module-tags">
              <span>想读</span><span>在读</span><span>已读</span>
            </div>
          </div>
          <div class="module-card animate-on-scroll aos-delay-2">
            <div class="module-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <line x1="6" y1="12" x2="10" y2="12"/><line x1="8" y1="10" x2="8" y2="14"/>
                <line x1="15" y1="13" x2="15.01" y2="13"/><line x1="18" y1="11" x2="18.01" y2="11"/>
                <path d="M17.32 5H6.68a4 4 0 0 0-3.98 3.59l-.7 6.41a2.5 2.5 0 0 0 4.22 2.05L8.5 15h7l2.28 2.05a2.5 2.5 0 0 0 4.22-2.05l-.7-6.41A4 4 0 0 0 17.32 5z"/>
              </svg>
            </div>
            <h3>游戏收藏</h3>
            <p>数字版、实体版、订阅制分类管理，记录游玩时长与截图画廊，收藏每段冒险。</p>
            <div class="module-tags">
              <span>想玩</span><span>在玩</span><span>已通关</span>
            </div>
          </div>
          <div class="module-card animate-on-scroll aos-delay-3">
            <div class="module-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </div>
            <h3>笔记整理</h3>
            <p>Markdown 编辑实时渲染，支持置顶与标签，列表、瀑布流、时间线随心切换。</p>
            <div class="module-tags">
              <span>Markdown</span><span>标签</span><span>置顶</span>
            </div>
          </div>
        </div>
        <div class="modules-cta animate-on-scroll">
          <router-link to="/features" class="btn btn-outline">查看全部功能</router-link>
        </div>
      </div>
    </section>

    <!-- Screenshots -->
    <section class="screenshots">
      <div class="container">
        <span class="section-eyebrow animate-on-scroll" style="text-align:center">SCREENSHOTS</span>
        <h2 class="section-title animate-on-scroll">一览应用</h2>
        <p class="section-subtitle animate-on-scroll">Material 3 极简设计，明暗主题与多套配色随心切换</p>
        <div class="shots-row">
          <div class="shot animate-on-scroll">
            <div class="shot-frame">
              <img src="~@/assets/img/screen_drawer.jpg" alt="侧边栏" loading="lazy" />
            </div>
            <span class="shot-label">侧边栏</span>
          </div>
          <div class="shot shot-main animate-on-scroll aos-delay-1">
            <div class="shot-frame">
              <img src="~@/assets/img/screen_home.jpg" alt="主界面" loading="lazy" />
            </div>
            <span class="shot-label">主界面</span>
          </div>
          <div class="shot animate-on-scroll aos-delay-2">
            <div class="shot-frame">
              <img src="~@/assets/img/screen_profile.jpg" alt="我的" loading="lazy" />
            </div>
            <span class="shot-label">个人中心</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Highlights -->
    <section class="highlights">
      <div class="container">
        <div class="highlight-row animate-on-scroll">
          <div class="highlight-content">
            <span class="section-eyebrow">PRIVACY FIRST</span>
            <h3>数据，完全由你掌控</h3>
            <p>所有数据存储在本地 SQLite 数据库，无需注册账号，不收集任何个人信息。多重备份机制，让珍贵记录永不丢失。</p>
            <ul class="highlight-list">
              <li>本地存储，离线可用，隐私零收集</li>
              <li>WebDAV 云同步，支持双向与定时自动同步</li>
              <li>本地 ZIP 备份与定时自动备份（保留最近 5 份）</li>
              <li>回收站机制，误删内容随时找回</li>
            </ul>
          </div>
          <div class="highlight-visual">
            <div class="visual-card dark">
              <div class="backup-item">
                <span class="bi-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                </span>
                <span>数据统计</span>
                <span class="arrow">›</span>
              </div>
              <div class="backup-item">
                <span class="bi-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </span>
                <span>本地备份</span>
                <span class="arrow">›</span>
              </div>
              <div class="backup-item">
                <span class="bi-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
                </span>
                <span>WebDAV 云同步</span>
                <span class="arrow">›</span>
              </div>
              <div class="backup-item">
                <span class="bi-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                </span>
                <span>回收站</span>
                <span class="arrow">›</span>
              </div>
            </div>
          </div>
        </div>
        <div class="highlight-row reverse animate-on-scroll">
          <div class="highlight-content">
            <span class="section-eyebrow">PERSONALIZATION</span>
            <h3>你的风格，由你定义</h3>
            <p>从配色到排版，从布局到详情页样式，MookNote 提供丰富的个性化选项，打造属于你的记录空间。</p>
            <ul class="highlight-list">
              <li>6 套精心调配的配色方案 + Monet 动态取色</li>
              <li>暗色 / 亮色 / 跟随系统主题</li>
              <li>4 种详情页样式：标准 / 毛玻璃 / 浅色极简 / 豆瓣风</li>
              <li>字体与排版自定义，应用图标随心换</li>
            </ul>
          </div>
          <div class="highlight-visual">
            <div class="visual-card palette-card">
              <div class="palette-title">配色方案</div>
              <div class="palette-grid">
                <span class="swatch" style="background:#333333"></span>
                <span class="swatch" style="background:#3F51B5"></span>
                <span class="swatch" style="background:#009688"></span>
                <span class="swatch" style="background:#FF8F00"></span>
                <span class="swatch" style="background:#E91E63"></span>
                <span class="swatch" style="background:#673AB7"></span>
                <span class="swatch" style="background:#8B6F47"></span>
                <span class="swatch swatch-monet">M</span>
              </div>
              <div class="palette-divider"></div>
              <div class="palette-theme">
                <span class="theme-chip active">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="13" height="13"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                  亮色
                </span>
                <span class="theme-chip">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="13" height="13"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                  暗色
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Download Section -->
    <section id="download" class="download">
      <div class="container">
        <span class="section-eyebrow animate-on-scroll" style="text-align:center">DOWNLOAD</span>
        <h2 class="section-title animate-on-scroll">开始使用</h2>
        <p class="section-subtitle animate-on-scroll">支持 Android、Android Pad、Windows 与 Linux 多平台，开箱即用</p>
        <div class="download-options">
          <div class="download-card animate-on-scroll">
            <div class="download-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 0 0-.1521-.5676.416.416 0 0 0-.5676.1521l-2.0225 3.503C15.5902 8.4796 13.8533 8.208 12 8.208c-1.8533 0-3.5902.2716-5.1368.7621L4.8407 5.4671a.416.416 0 0 0-.5676-.1521.416.416 0 0 0-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589.3432 18.6617h23.3136c0-4.0028-2.3457-7.475-5.775-9.3403"/>
              </svg>
            </div>
            <h3>Android</h3>
            <p>支持 Android 5.0 及以上版本</p>
            <div class="dl-dropdown" ref="androidDropdown">
              <button class="btn btn-primary" @click.stop="androidMenuOpen = !androidMenuOpen">
                下载 APK
                <svg :class="['dl-chevron', { open: androidMenuOpen }]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              <transition name="dl-menu">
                <div v-show="androidMenuOpen" class="dl-menu">
                  <a
                    v-for="opt in androidVersions"
                    :key="opt.url"
                    :href="opt.url"
                    class="dl-option"
                    @click="androidMenuOpen = false"
                  >
                    <span class="dl-name">
                      {{ opt.name }}
                      <em v-if="opt.tag" :class="['dl-tag', { accent: opt.recommend }]">{{ opt.tag }}</em>
                    </span>
                    <span class="dl-desc">{{ opt.desc }}</span>
                  </a>
                </div>
              </transition>
            </div>
          </div>
          <div class="download-card animate-on-scroll aos-delay-1">
            <div class="download-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <line x1="18" y1="8" x2="18" y2="16"/>
              </svg>
            </div>
            <h3>Android Pad</h3>
            <p>专为平板大屏优化适配</p>
            <a href="https://mooknote.iletter.top/download/mooknote-pad.apk" class="btn btn-primary">下载 APK</a>
          </div>
          <div class="download-card animate-on-scroll aos-delay-2">
            <div class="download-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
              </svg>
            </div>
            <h3>Windows</h3>
            <p>解压后运行 mooknote.exe 即可</p>
            <a href="https://mooknote.iletter.top/download/mooknote.rar" class="btn btn-primary">下载 RAR</a>
          </div>
          <div class="download-card animate-on-scroll aos-delay-3">
            <div class="download-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="4 17 10 11 4 5"/>
                <line x1="12" y1="19" x2="20" y2="19"/>
              </svg>
            </div>
            <h3>Linux</h3>
            <p>解压后运行 ./mooknote 即可</p>
            <a href="https://mooknote.iletter.top/download/mooknote-linux.zip" class="btn btn-primary">下载 ZIP</a>
          </div>
        </div>
        <div class="version-info animate-on-scroll">
          <p>当前版本 {{ latestVersion }}<template v-if="latestDate"> · 更新于 {{ latestDate }}</template></p>
        </div>
      </div>
    </section>

    <!-- Closing CTA -->
    <section class="closing">
      <div class="container closing-inner animate-on-scroll">
        <h2>现在开始，收藏每一种热爱</h2>
        <p>免费 · 开源 · 无需注册，下载即可使用</p>
        <div class="closing-actions">
          <a href="#download" class="btn btn-light" @click.prevent="scrollToDownload">免费下载</a>
          <a href="https://github.com/dellevin/mooknote" target="_blank" rel="noopener" class="closing-link">
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>
            GitHub
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      latestVersion: 'v0.3.5',
      latestDate: '',
      androidMenuOpen: false,
      androidVersions: [
        {
          name: '通用版',
          tag: '省心之选',
          recommend: false,
          desc: '包含全部 CPU 架构，体积较大，不知道选哪个就选它',
          url: 'https://mooknote.iletter.top/download/mooknote.apk'
        },
        {
          name: 'ARM64 版',
          tag: '推荐',
          recommend: true,
          desc: '近几年的安卓手机基本都是此架构，体积更小',
          url: 'https://mooknote.iletter.top/download/app-arm64-v8a-release.apk'
        },
        {
          name: 'ARMv7 版',
          tag: '',
          recommend: false,
          desc: '适配较老旧的 32 位安卓设备',
          url: 'https://mooknote.iletter.top/download/app-armeabi-v7a-release.apk'
        },
        {
          name: 'x86_64 版',
          tag: '',
          recommend: false,
          desc: '适用于安卓模拟器或部分 Intel 芯片设备',
          url: 'https://mooknote.iletter.top/download/app-x86_64-release.apk'
        }
      ]
    }
  },
  mounted() {
    fetch('https://api.mooknote.iletter.top/api/changelog')
      .then(res => res.json())
      .then(data => {
        if (data.items && data.items.length) {
          const latest = data.items.reduce((max, item) =>
            (item.sort || 0) > (max.sort || 0) ? item : max
          )
          this.latestVersion = latest.version
          this.latestDate = latest.date
        }
      })
      .catch(() => {})

    this.initScrollObserver()
    document.addEventListener('click', this.closeAndroidMenu)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeAndroidMenu)
  },
  methods: {
    closeAndroidMenu(e) {
      if (this.androidMenuOpen && this.$refs.androidDropdown && !this.$refs.androidDropdown.contains(e.target)) {
        this.androidMenuOpen = false
      }
    },
    scrollToDownload() {
      const el = document.getElementById('download')
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    initScrollObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.1 })

      this.$nextTick(() => {
        const animatedElements = this.$el.querySelectorAll('.animate-on-scroll')
        animatedElements.forEach(el => observer.observe(el))
      })
    }
  }
}
</script>

<style scoped>
/* ===== Hero ===== */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 140px 24px 100px;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(720px 480px at 85% 8%, rgba(0, 102, 255, 0.06), transparent 65%),
    radial-gradient(640px 480px at 8% 85%, rgba(16, 16, 20, 0.04), transparent 65%),
    linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 70%);
}

.hero-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(16, 16, 20, 0.07) 1px, transparent 1px);
  background-size: 26px 26px;
  mask-image: linear-gradient(180deg, rgba(0,0,0,0.5), transparent 55%);
  -webkit-mask-image: linear-gradient(180deg, rgba(0,0,0,0.5), transparent 55%);
}

.hero-inner {
  position: relative;
  display: flex;
  align-items: center;
  gap: 64px;
  width: 100%;
}

.hero-content {
  flex: 1.1;
  max-width: 600px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 1px;
  color: var(--color-text-secondary);
  box-shadow: var(--shadow-sm);
  margin-bottom: 28px;
}

.badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.hero-title {
  font-size: clamp(38px, 5.2vw, 60px);
  font-weight: 700;
  letter-spacing: -1.5px;
  line-height: 1.15;
  margin-bottom: 22px;
  color: var(--color-text);
}

.hero-desc {
  font-size: 16.5px;
  color: var(--color-text-secondary);
  line-height: 1.9;
  margin-bottom: 36px;
  max-width: 480px;
}

.hero-actions {
  display: flex;
  gap: 14px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.hero-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-pill {
  padding: 5px 12px;
  background: var(--color-text);
  color: #fff;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.meta-item {
  font-size: 13px;
  color: var(--color-text-muted);
  padding-left: 12px;
  position: relative;
}

.meta-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--color-border-strong);
}

/* Phone mockup */
.hero-visual {
  flex: 1;
  display: none;
  justify-content: center;
  position: relative;
}

.phone-mockup {
  position: relative;
  z-index: 1;
  transform: rotate(2deg);
  transition: transform 0.5s var(--ease-out);
}

.hero-visual:hover .phone-mockup {
  transform: rotate(0deg) translateY(-6px);
}

.phone-frame {
  width: 264px;
  padding: 10px;
  background: #161617;
  border-radius: 42px;
  box-shadow:
    0 2px 4px rgba(16, 16, 20, 0.1),
    0 32px 64px -24px rgba(16, 16, 20, 0.35),
    inset 0 0 0 2px rgba(255, 255, 255, 0.08);
}

.phone-screen {
  display: block;
  width: 100%;
  border-radius: 33px;
  aspect-ratio: 1440 / 3120;
  object-fit: cover;
  object-position: top center;
  background: #fff;
}

.float-card {
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(236, 236, 237, 0.9);
  border-radius: 18px;
  box-shadow: var(--shadow-lg);
  animation: floaty 5s ease-in-out infinite;
}

.float-card-1 {
  top: 18%;
  left: -4%;
}

.float-card-2 {
  bottom: 16%;
  right: -2%;
  animation-delay: 2.5s;
}

@keyframes floaty {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.fc-icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: var(--color-text);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fc-icon.accent {
  background: var(--color-accent);
}

.fc-icon svg {
  width: 18px;
  height: 18px;
}

.fc-value {
  display: block;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--color-text);
}

.fc-label {
  font-size: 12px;
  color: var(--color-text-muted);
}

/* ===== Modules ===== */
.modules {
  padding: 110px 24px;
  background: #fff;
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 52px;
}

.module-card {
  position: relative;
  padding: 32px 26px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: transform 0.35s var(--ease-out), box-shadow 0.35s var(--ease-out), border-color 0.35s;
}

.module-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 26px;
  right: 26px;
  height: 2px;
  background: var(--color-text);
  border-radius: 2px;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.35s var(--ease-out);
}

.module-card:hover {
  transform: translateY(-6px);
  background: #fff;
  border-color: var(--color-border-strong);
  box-shadow: var(--shadow-lg);
}

.module-card:hover::before {
  transform: scaleX(1);
}

.module-icon {
  width: 52px;
  height: 52px;
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  margin-bottom: 22px;
  transition: all 0.35s var(--ease-out);
}

.module-card:hover .module-icon {
  background: var(--color-text);
  border-color: var(--color-text);
  color: #fff;
  transform: scale(1.06);
}

.module-icon svg {
  width: 25px;
  height: 25px;
}

.module-card h3 {
  font-size: 19px;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--color-text);
}

.module-card p {
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin-bottom: 20px;
}

.module-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.module-tags span {
  padding: 4px 11px;
  font-size: 12px;
  color: var(--color-text-secondary);
  background: var(--color-bg-tertiary);
  border-radius: 999px;
  transition: all 0.3s;
}

.module-card:hover .module-tags span {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
}

.modules-cta {
  text-align: center;
}

/* ===== Screenshots ===== */
.screenshots {
  padding: 110px 24px 130px;
  background: var(--color-bg-secondary);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  overflow: hidden;
}

.shots-row {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 36px;
}

.shot {
  text-align: center;
}

.shot-frame {
  width: 216px;
  padding: 8px;
  background: #161617;
  border-radius: 34px;
  box-shadow: var(--shadow-lg);
  transition: transform 0.4s var(--ease-out);
  margin-top: 36px;
}

.shot:hover .shot-frame {
  transform: translateY(-8px);
}

.shot-main .shot-frame {
  width: 248px;
  margin-top: 0;
  box-shadow: 0 2px 4px rgba(16,16,20,0.1), 0 40px 80px -24px rgba(16,16,20,0.4);
}

.shot-frame img {
  display: block;
  width: 100%;
  border-radius: 27px;
  aspect-ratio: 1440 / 3120;
  object-fit: cover;
  object-position: top center;
  background: #fff;
}

.shot-label {
  display: inline-block;
  margin-top: 18px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-muted);
  letter-spacing: 1px;
}

/* ===== Highlights ===== */
.highlights {
  padding: 110px 24px;
  background: #fff;
}

.highlight-row {
  display: flex;
  align-items: center;
  gap: 88px;
  margin-bottom: 110px;
}

.highlight-row:last-child {
  margin-bottom: 0;
}

.highlight-row.reverse {
  flex-direction: row-reverse;
}

.highlight-content {
  flex: 1;
}

.highlight-content .section-eyebrow {
  margin-bottom: 12px;
}

.highlight-content h3 {
  font-size: clamp(26px, 3vw, 34px);
  font-weight: 700;
  letter-spacing: -0.5px;
  margin-bottom: 18px;
  color: var(--color-text);
}

.highlight-content p {
  font-size: 15.5px;
  color: var(--color-text-secondary);
  line-height: 1.9;
  margin-bottom: 26px;
}

.highlight-list {
  list-style: none;
}

.highlight-list li {
  padding: 9px 0 9px 30px;
  position: relative;
  font-size: 14.5px;
  color: var(--color-text-secondary);
}

.highlight-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 14px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--color-accent-soft) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230066FF' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E") center / 10px no-repeat;
}

.highlight-visual {
  flex: 1;
  display: flex;
  justify-content: center;
}

.visual-card {
  width: 320px;
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 26px;
  box-shadow: var(--shadow-md);
  transition: transform 0.35s var(--ease-out), box-shadow 0.35s var(--ease-out);
}

.visual-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.visual-card.dark {
  background: #161617;
  border-color: #161617;
  color: #fff;
}

.backup-item {
  display: flex;
  align-items: center;
  padding: 15px 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 14.5px;
  transition: padding 0.25s var(--ease-out);
}

.backup-item:last-child {
  border-bottom: none;
}

.backup-item:hover {
  padding-left: 8px;
}

.bi-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.bi-icon svg {
  width: 16px;
  height: 16px;
}

.arrow {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.4);
  transition: transform 0.25s var(--ease-out), color 0.25s;
}

.backup-item:hover .arrow {
  transform: translateX(4px);
  color: #fff;
}

/* Palette card */
.palette-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 18px;
}

.palette-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.swatch {
  aspect-ratio: 1;
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px rgba(16, 16, 20, 0.06);
  transition: transform 0.25s var(--ease-out);
  cursor: pointer;
}

.swatch:hover {
  transform: scale(1.1);
}

.swatch-monet {
  background: linear-gradient(135deg, #a8c7fa 0%, #d3e3fd 50%, #7fcdf3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: #0b305f;
}

.palette-divider {
  height: 1px;
  background: var(--color-border);
  margin: 22px 0 16px;
}

.palette-theme {
  display: flex;
  gap: 10px;
}

.theme-chip {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  background: var(--color-bg-tertiary);
}

.theme-chip.active {
  background: var(--color-text);
  color: #fff;
}

/* ===== Download ===== */
.download {
  position: relative;
  z-index: 2;
  padding: 110px 24px;
  background: var(--color-bg-secondary);
  border-top: 1px solid var(--color-border);
  text-align: center;
}

.download-options {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 36px;
}

.download-card {
  position: relative;
  flex: 1;
  min-width: 0;
  padding: 40px 24px;
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  text-align: center;
  transition: transform 0.35s var(--ease-out), box-shadow 0.35s var(--ease-out);
}

.download-card:focus-within {
  z-index: 5;
}

.download-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-lg);
}

.download-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 22px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.35s var(--ease-out);
}

.download-card:hover .download-icon {
  background: var(--color-text);
  border-color: var(--color-text);
  color: #fff;
  transform: scale(1.06);
}

.download-icon svg {
  width: 30px;
  height: 30px;
}

.download-card h3 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.download-card p {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 26px;
}

.version-info {
  font-size: 13.5px;
  color: var(--color-text-muted);
}

/* Android version dropdown */
.dl-dropdown {
  position: relative;
  display: inline-block;
}

.dl-chevron {
  transition: transform 0.3s var(--ease-out);
}

.dl-chevron.open {
  transform: rotate(180deg);
}

.dl-menu {
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  width: min(320px, 84vw);
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  padding: 6px;
  z-index: 60;
  text-align: left;
}

.dl-menu-enter-active,
.dl-menu-leave-active {
  transition: opacity 0.25s var(--ease-out), transform 0.25s var(--ease-out);
}

.dl-menu-enter,
.dl-menu-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-6px) scale(0.98);
}

.dl-option {
  display: block;
  padding: 11px 14px;
  border-radius: 11px;
  text-decoration: none;
  transition: background 0.2s;
}

.dl-option:hover {
  background: var(--color-bg-secondary);
}

.dl-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 2px;
}

.dl-tag {
  font-style: normal;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
}

.dl-tag.accent {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.dl-desc {
  display: block;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--color-text-muted);
}

/* ===== Closing CTA ===== */
.closing {
  position: relative;
  z-index: 1;
  padding: 24px;
  background: var(--color-bg-secondary);
}

.closing-inner {
  background: #161617;
  border-radius: 32px;
  padding: 96px 32px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.closing-inner::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(560px 320px at 50% -10%, rgba(0, 102, 255, 0.22), transparent 70%);
}

.closing-inner h2 {
  position: relative;
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  letter-spacing: -1px;
  color: #fff;
  margin-bottom: 14px;
}

.closing-inner p {
  position: relative;
  font-size: 15.5px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 36px;
}

.closing-actions {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.closing-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: color 0.2s;
}

.closing-link:hover {
  color: #fff;
}

/* ===== Responsive ===== */
@media (min-width: 1024px) {
  .hero-visual {
    display: flex;
  }
}

@media (max-width: 1023px) {
  .hero {
    min-height: auto;
    padding-top: 130px;
  }

  .hero-inner {
    justify-content: center;
    text-align: center;
  }

  .hero-content {
    max-width: 640px;
  }

  .hero-desc {
    margin-left: auto;
    margin-right: auto;
  }

  .hero-actions,
  .hero-meta {
    justify-content: center;
  }

  .modules-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 860px) {
  .shots-row {
    gap: 20px;
  }

  .shot:not(.shot-main) {
    display: none;
  }

  .highlight-row,
  .highlight-row.reverse {
    flex-direction: column;
    gap: 44px;
  }

  .download-options {
    flex-direction: column;
    align-items: center;
  }

  .download-card {
    width: 100%;
    max-width: 360px;
  }

  .closing {
    padding: 12px;
  }

  .closing-inner {
    padding: 72px 24px;
    border-radius: 24px;
  }
}

@media (max-width: 560px) {
  .modules-grid {
    grid-template-columns: 1fr;
  }
}
</style>
